import { activeOptions } from 'components/aggregatorManagement/utils'
import {
  Dropdown,
  FormLayout,
  Spinner,
  StoreSelect,
  TextInput,
} from 'components/common'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { LAST_MILES, OrderErpSource, LastMiles, storeFormFields } from './utils'
import { useEffect, useMemo, useState } from 'react'
import { fetchClusters, fetchEmirates } from 'utils/api/configApi'
import LastMileDropdown from './lastMileDropdown'
import { fetchAggregators } from 'utils/api/aggregatorApi'
import { CustomObject } from 'types/common'
interface StoreDetailsFormProps {
  register: UseFormRegister<any>
  errors: FieldErrors<any>
  setValue: UseFormSetValue<any>
  store?: any
  isOptional?: boolean
  isCreateForm?: boolean
  canEditSapStore?: boolean
}

// Return Order Last Mile Options
const orderLastMileOptions = LAST_MILES.map((item) => ({
  label: item,
  value: item,
}))

const pharmacyOptions = [
  { label: 'Yes', value: true, disabled: false },
  { label: 'No', value: false, disabled: false },
]

const {
  STORE_ID,
  STORE_IS_ACTIVE,
  STORE_NAME,
  STORE_INSTASHOP_IDENTIFIER,
  STORE_LAST_MILE,
  ORDER_ERP_SOURCE,
  STORE_TALABAT_ID,
  STORE_FLOWER_TALABAT_ID,
  STORE_FOOD_TALABAT_ID,
  STORE_OOS_THRESHOLD,
  STORE_INVOICE_PREFIX_CODE,
  STORE_CLUSTER,
  DIRECT_POSTING_STORE,
  STORE_COORDINATES,
  SAP_STORE_CODE,
  STORE_EMIRATE,
  STORE_INVOICE_ADDRESS,
  STORE_INVOICE_LOGO,
  STORE_LOGINEXT_TOKEN,
  STORE_INVOICE_SUB_LOGO,
  STORE_AUTO_ALLOCATE,
  STORE_IS_THIRD_PARTY,
  OTP_PHONE_NUMBERS,
} = storeFormFields

const StoreDetailsForm = ({
  register,
  errors,
  store,
  setValue,
  isOptional = false,
  isCreateForm,
  canEditSapStore = true,
}: StoreDetailsFormProps) => {
  const [clustersOptions, setClusetersOptions] = useState([
    {
      label: '--NONE--',
      value: '',
      disabled: false,
    },
  ])

  const [emiratesOptions, setEmiratesOptions] = useState([
    {
      label: '--NONE--',
      value: '',
      disabled: false,
    },
  ])

  const [autoAllocateOptions] = useState([
    {
      label: 'Yes',
      value: 'Y',
      disabled: false,
    },
    {
      label: 'No',
      value: 'N',
      disabled: false,
    },
  ])

  const [isThirdParty, setIsThirdParty] = useState<boolean>(
    store?.[STORE_IS_THIRD_PARTY.key] ?? false,
  )

  const [logiNextMapping, setLogiNextMapping] = useState({})
  const [loading, setLoading] = useState<boolean>(false)

  const handleFetchClusters = async () => {
    const { data } = await fetchClusters()
    if (data?.clusters?.length) {
      const newClusters = data.clusters.map((cluster: string) => ({
        label: cluster,
        value: cluster,
      }))

      setClusetersOptions([...clustersOptions, ...newClusters])
    }
  }

  const orderErpOptions = useMemo(() => {
    return Object.values(OrderErpSource).map((item) => ({
      label: item,
      value: item,
      disabled: false,
    }))
  }, [])

  useEffect(() => {
    handleFetchClusters()
  }, [])

  useEffect(() => {
    const loadEmirates = async () => {
      try {
        const { data } = await fetchEmirates()

        setEmiratesOptions(data.availableEmirates)
      } catch {
        console.log('Failed to load emirates')
      }
    }

    loadEmirates()
  }, [])

  const getAggregators = async () => {
    try {
      setLoading(true)
      const { data } = await fetchAggregators({
        limit: 500,
        page: 1,
        isActive: true,
      })

      const aggregators = data?.docs

      if (!aggregators || aggregators.length <= 0) {
        return setLogiNextMapping([])
      }

      const defaultValue: CustomObject = {}

      Object.values(LastMiles).forEach((lastMile) => {
        defaultValue[lastMile] = []
      })

      defaultValue['Unknown'] = aggregators.map((agg: any) => ({
        _id: agg._id,
        aggregatorName: agg.aggregatorName,
      }))

      setLogiNextMapping(defaultValue)
    } catch (err) {
      setLogiNextMapping([])
    } finally {
      setLoading(false)
    }
  }

  const lastMileData = useMemo(() => {
    const hasExistingAssignments =
      store?.lastMile && Object.keys(store.lastMile).length > 0

    if (hasExistingAssignments) {
      return store.lastMile
    }

    // Create mode or toggled from third-party: use fresh fetch with all in Unknown
    return logiNextMapping
  }, [store?.lastMile, logiNextMapping])

  useEffect(() => {
    if (isCreateForm || !isThirdParty) getAggregators()
  }, [isCreateForm, isThirdParty])

  return (
    <>
      <div className="mb-1 flex items-start justify-between gap-5">
        <h3 className="title">Store Details</h3>

        {/* Store Active/Inactive */}
        <div className="w-full max-w-32">
          <Dropdown
            defaultValue={store?.[STORE_IS_ACTIVE.key] ?? true}
            setValue={setValue}
            options={activeOptions}
            name={STORE_IS_ACTIVE.key}
            error={errors?.[STORE_IS_ACTIVE.key]?.message as string}
          />
        </div>
      </div>

      <FormLayout>
        <div className="grid gap-x-5 gap-y-3 md:grid-cols-2 lg:grid-cols-3">
          {/* Store ID */}
          <TextInput
            required={!isOptional}
            disabled={isOptional}
            label={STORE_ID.label}
            register={register}
            name={STORE_ID.key}
            error={errors?.[STORE_ID.key]?.message as string}
          />

          {/* Store Name */}
          <TextInput
            required
            label={STORE_NAME.label}
            register={register}
            name={STORE_NAME.key}
            error={errors?.[STORE_NAME.key]?.message as string}
          />

          <Dropdown
            label={STORE_IS_THIRD_PARTY.label}
            defaultValue={store?.[STORE_IS_THIRD_PARTY.key] ?? false}
            setValue={(name, value) => {
              setValue(name, value)
              setIsThirdParty(value === true || value === 'true')
            }}
            options={pharmacyOptions}
            name={STORE_IS_THIRD_PARTY.key}
            error={errors?.[STORE_IS_THIRD_PARTY.key]?.message as string}
          />

          {/* Store OOS_THRESHOLD */}
          <TextInput
            required
            label={STORE_OOS_THRESHOLD.label}
            register={register}
            name={STORE_OOS_THRESHOLD.key}
            error={errors?.[STORE_OOS_THRESHOLD.key]?.message as string}
          />
          {/* ✅ Emirates Dropdown */}
          {!isThirdParty && (
            <>
              <Dropdown
                key={emiratesOptions.length}
                required
                defaultValue={store?.[STORE_EMIRATE.key]}
                label={STORE_EMIRATE.label || 'Emirate'}
                setValue={setValue}
                options={emiratesOptions}
                name={STORE_EMIRATE.key}
                error={errors?.[STORE_EMIRATE.key]?.message as string}
              />

              <TextInput
                label={STORE_COORDINATES.label}
                placeholder="e.g. 25.2048,55.2708"
                register={register}
                name={STORE_COORDINATES.key}
                error={
                  (errors as any)?.location?.coordinates?.message as string
                }
              />
            </>
          )}
        </div>
      </FormLayout>
      {!isThirdParty && (
        <>
          <h3 className="title">Invoice Config</h3>

          <FormLayout>
            <div className="grid gap-x-5 gap-y-3 md:grid-cols-2">
              {/* Invoice Prefix Code */}
              <TextInput
                required
                register={register}
                label={STORE_INVOICE_PREFIX_CODE.label}
                name={STORE_INVOICE_PREFIX_CODE.key}
                error={
                  errors?.[STORE_INVOICE_PREFIX_CODE.key]?.message as string
                }
              />

              <TextInput
                required={false}
                label={STORE_INVOICE_ADDRESS.label}
                register={register}
                name={STORE_INVOICE_ADDRESS.key}
                error={errors?.[STORE_INVOICE_ADDRESS.key]?.message as string}
              />

              <TextInput
                required={false}
                label={STORE_INVOICE_LOGO.label}
                register={register}
                name={STORE_INVOICE_LOGO.key}
                error={errors?.[STORE_INVOICE_LOGO.key]?.message as string}
              />

              <TextInput
                required={false}
                label={STORE_INVOICE_SUB_LOGO.label}
                register={register}
                name={STORE_INVOICE_SUB_LOGO.key}
                error={errors?.[STORE_INVOICE_SUB_LOGO.key]?.message as string}
              />

              <div className="md:col-span-2">
                <TextInput
                  required={false}
                  label={OTP_PHONE_NUMBERS.label}
                  register={register}
                  name={OTP_PHONE_NUMBERS.key}
                  placeholder="e.g. 971501234567,971509876543"
                  hintText="These numbers will receive the invoice OTP via SMS when the print limit is reached."
                  error={errors?.[OTP_PHONE_NUMBERS.key]?.message as string}
                />
              </div>
            </div>
          </FormLayout>

          <h3 className="title">Aggregator Mapping</h3>

          <FormLayout>
            <div className="grid gap-x-5 gap-y-3 md:grid-cols-2">
              {/* Store IDENTIFIER */}
              <TextInput
                label={STORE_INSTASHOP_IDENTIFIER.label}
                register={register}
                name={STORE_INSTASHOP_IDENTIFIER.key}
                error={
                  errors?.[STORE_INSTASHOP_IDENTIFIER.key]?.message as string
                }
              />

              {/* Store TALABAT ID */}
              <TextInput
                label={STORE_TALABAT_ID.label}
                register={register}
                name={STORE_TALABAT_ID.key}
                error={errors?.[STORE_TALABAT_ID.key]?.message as string}
              />

              {/* Store FLOWER TALABAT ID */}
              <TextInput
                label={STORE_FLOWER_TALABAT_ID.label}
                register={register}
                name={STORE_FLOWER_TALABAT_ID.key}
                error={errors?.[STORE_FLOWER_TALABAT_ID.key]?.message as string}
              />

              {/* Store FOOD TALABAT ID */}
              <TextInput
                label={STORE_FOOD_TALABAT_ID.label}
                register={register}
                name={STORE_FOOD_TALABAT_ID.key}
                error={errors?.[STORE_FOOD_TALABAT_ID.key]?.message as string}
              />
            </div>
          </FormLayout>
        </>
      )}
      <h3 className="title">Other Details</h3>

      <FormLayout>
        <div className="grid gap-x-5 gap-y-3 md:grid-cols-2 ">
          {/* Order ERP Source */}
          {!isThirdParty && (
            <>
              <Dropdown
                defaultValue={
                  store?.[ORDER_ERP_SOURCE.key] ?? orderErpOptions[0]
                }
                label={ORDER_ERP_SOURCE.label}
                setValue={setValue}
                options={orderErpOptions}
                name={ORDER_ERP_SOURCE.key}
                error={errors?.[ORDER_ERP_SOURCE.key]?.message as string}
              />

              {/* SAP STORE ID */}
              <TextInput
                label={SAP_STORE_CODE.label}
                register={register}
                name={SAP_STORE_CODE.key}
                error={errors?.[SAP_STORE_CODE.key]?.message as string}
                disabled={!canEditSapStore}
              />

              <StoreSelect
                label={DIRECT_POSTING_STORE.label}
                name={DIRECT_POSTING_STORE.key}
                error={errors?.[DIRECT_POSTING_STORE.key]?.message as string}
                setValue={setValue}
                defaultValue={store?.[DIRECT_POSTING_STORE.key]}
                storeId={store?.[STORE_ID.key]}
              />
            </>
          )}
          {/* Cluster */}
          {clustersOptions.length > 1 && (
            <Dropdown
              defaultValue={store?.[STORE_CLUSTER.key]}
              label={STORE_CLUSTER.label}
              setValue={setValue}
              options={clustersOptions}
              name={STORE_CLUSTER.key}
              error={errors?.[STORE_CLUSTER.key]?.message as string}
            />
          )}
        </div>
      </FormLayout>
      {!isThirdParty && (
        <>
          <h3 className="title">Last Mile Config </h3>

          <FormLayout>
            <>
              <div className="mb-5 grid gap-x-5 gap-y-3 md:grid-cols-2">
                <Dropdown
                  defaultValue={store?.[STORE_AUTO_ALLOCATE.key]}
                  label={STORE_AUTO_ALLOCATE.label}
                  setValue={setValue}
                  options={autoAllocateOptions}
                  name={STORE_AUTO_ALLOCATE.key}
                  error={errors?.[STORE_AUTO_ALLOCATE.key]?.message as string}
                />

                <TextInput
                  required={false}
                  label={STORE_LOGINEXT_TOKEN.label}
                  register={register}
                  name={STORE_LOGINEXT_TOKEN.key}
                  error={errors?.[STORE_LOGINEXT_TOKEN.key]?.message as string}
                />
              </div>

              {/* Order Last Mile */}
              {loading ? (
                <Spinner />
              ) : (
                <LastMileDropdown
                  defaultValue={store?.[STORE_LAST_MILE.key]}
                  label={STORE_LAST_MILE.label}
                  setValue={setValue}
                  options={orderLastMileOptions}
                  name={STORE_LAST_MILE.key}
                  error={errors?.[STORE_LAST_MILE.key]?.message as string}
                  store={{ ...store, lastMile: lastMileData }}
                  // store={store || { lastMile: logiNextMapping }}
                />
              )}
            </>
          </FormLayout>
        </>
      )}
    </>
  )
}

export default StoreDetailsForm
